import type { StytchAPIError } from '@stytch/vanilla-js';
import React, { useEffect, useRef, useState } from 'react';
import { AppState } from 'react-native';
import { isWeb, isWindowDefined, View } from 'smoothie';

import { SESSION_DURATION_MINUTES } from '../../auth/constants';
import { useStytch, useStytchSession } from '../../auth/hooks/useAuth';
import type { ContainerComponent } from '../../common/types/Common';
import { captureException, captureMessage } from '../../logger/Logger';

const SESSION_RENEWAL_INTERVAL = 120000; // 2 minutes
const ENABLE_SESSION_RENEWAL_ON_LAUNCH = !isWeb;

const isUnauthenticatedStytchError = (error: unknown): boolean => {
    if (error == null || typeof error !== 'object') {
        return false;
    }

    const stytchError = error as Partial<StytchAPIError>;
    return (
        stytchError.status_code === 401 ||
        stytchError.error_type === 'user_unauthenticated'
    );
};

const isAppInBackground = (): boolean => {
    if (isWeb) {
        return isWindowDefined && document.hidden;
    }

    return AppState.currentState !== 'active';
};

// this component will revoke any persisted sessions on launch and set up automatic renewing of sessions for us
const SessionManager: ContainerComponent = ({ children }) => {
    const [isClearingSession, setIsClearingSession] = useState(
        ENABLE_SESSION_RENEWAL_ON_LAUNCH,
    );

    const stytchClient = useStytch();
    const stytchSession = useStytchSession();

    // We need to keep a ref of the session because setInterval cannot detect state changes
    const stytchSessionRef = useRef(stytchSession);

    useEffect(() => {
        let interval: NodeJS.Timeout;

        const renewSession = async () => {
            if (!stytchSessionRef.current.session) {
                return;
            }

            // Background tabs/apps throttle timers; skip renewals until foregrounded again.
            if (isAppInBackground()) {
                return;
            }

            try {
                await stytchClient.session.authenticate({
                    session_duration_minutes: SESSION_DURATION_MINUTES,
                });
            } catch (error) {
                if (isUnauthenticatedStytchError(error)) {
                    // Expected when the server session expired while local state still looks signed-in.
                    captureMessage(
                        'Stytch session expired during renewal; skipping authenticate',
                    );
                    return;
                }

                captureException(error);
            }
        };

        void (async () => {
            // Stytch by default persists sessions on devices between app launches, which is not ideal for us on mobile
            // If enabled, let's revoke any persisted session on launch
            try {
                if (ENABLE_SESSION_RENEWAL_ON_LAUNCH && stytchSession.session) {
                    await stytchClient.session.revoke();
                }
            } catch (e) {
                captureException(
                    new Error('Error clearing existing stytch session'),
                    { error: e },
                );
            } finally {
                setIsClearingSession(false);
            }

            // Sets up a timer to renew an active session at a given interval
            // Note: <InactivityDetector> will still revoke an active session if it doesn't detect gestures within a certain threshold
            interval = setInterval(() => {
                void renewSession();
            }, SESSION_RENEWAL_INTERVAL);
        })();

        const handleVisibilityChange = () => {
            if (!document.hidden) {
                void renewSession();
            }
        };

        const appStateSubscription = !isWeb
            ? AppState.addEventListener('change', (nextAppState) => {
                  if (nextAppState === 'active') {
                      void renewSession();
                  }
              })
            : undefined;

        if (isWeb && isWindowDefined) {
            document.addEventListener(
                'visibilitychange',
                handleVisibilityChange,
            );
        }

        return () => {
            clearInterval(interval);
            appStateSubscription?.remove();
            if (isWeb && isWindowDefined) {
                document.removeEventListener(
                    'visibilitychange',
                    handleVisibilityChange,
                );
            }
        };
    }, [stytchClient]);

    // Keep the session ref in sync with state
    useEffect(() => {
        stytchSessionRef.current = stytchSession;
    }, [stytchSession]);

    return isClearingSession ? null : <View flex={1}>{children}</View>;
};

export default SessionManager;
